import { _t } from "../../languageHandler";
import { DthHttpLicenseClient } from "../clientHttp/DthHttpLicenseClient";
import { DthSessionStorage } from "../clientHttp/DthSessionStorage";
import { IDthFetchDomainInfoRequest, IDthValidateLicenseRequest } from "../clientHttp/models/httpClientLicenseModels";
import { LicenseJWTPayload, verifyJwt } from "../clientHttp/utils/utils";
import { LICENSE_JWT_KEY, LICENSE_JWT_LOGIN_KEY } from "../constants";


interface LicenseManagerOptions {
    bundleId: string;
    domain: string;
    publicKey: string;
}

export class LicenseManager {
    private static instance: LicenseManager | null = null;
    private static bundleId: string | null = null;  // Bundle ID statico per inizializzazione
    private static domain: string | null = null;  // Domain statico per inizializzazione
    private static publicKey: string | null = null;  // Public key statica per inizializzazione

    /**
     * Costruttore privato, per forzare l'uso del singleton. 
     * Verifica che i parametri statici siano stati inizializzati.
     * Lancia un errore se si tenta di creare un'istanza senza inizializzazione o se i parametri non sono validi.
     */
    private constructor() {
        if (!LicenseManager.bundleId ||
            !LicenseManager.domain ||
            !LicenseManager.publicKey) {
            throw new Error(_t("http_client_errors.parameters_must_be_set_before_creating_the_instance"));
        }
    }

    /**
     * Inizializza i parametri statici necessari per il funzionamento del LicenseManager. 
     * Deve essere chiamato prima di ottenere l'istanza.
     * @param options Parametri di inizializzazione
     */
    public static initialize(options: LicenseManagerOptions): void {
        if (LicenseManager.bundleId || LicenseManager.domain || LicenseManager.publicKey) {
            throw new Error(_t("http_client_errors.license_manager_already_initialized"));
        }

        // Rimuove eventuali slash finali dall'URL
        LicenseManager.bundleId = options.bundleId;
        LicenseManager.domain = options.domain.replace(/\/+$/, "");
        LicenseManager.publicKey = options.publicKey;
    }

    /**
     * Metodo di reset per test, che azzera l'istanza e i parametri statici. Usare con cautela!
     */
    public static reset(): void {
        LicenseManager.instance = null;
        LicenseManager.bundleId = null;
        LicenseManager.domain = null;
        LicenseManager.publicKey = null;
    }

    /**
     * Metodo statico per ottenere l'istanza del LicenseManager.
     * Se l'istanza non esiste, viene creata.
     * @returns L'istanza del LicenseManager
     */
    public static getInstance(): LicenseManager {
        if (!LicenseManager.instance) {
            LicenseManager.instance = new LicenseManager();
        }
        return LicenseManager.instance;
    }

    /**
     * Ottiene il JWT di licenza, prima da session storage, altrimenti lo recupera via HTTP e lo salva.
     * @param props Parametri per fetchDomainInfo
     */
    public static async getOrFetchDomainLicenseJwt()
        : Promise<LicenseJWTPayload> {
        const client = DthHttpLicenseClient.getInstance();
        const res = await client.fetchDomainInfo(
            { bundleId: LicenseManager.bundleId!, domain: LicenseManager.domain! } as IDthFetchDomainInfoRequest
        );
        const jwt = res.jwt;
        if (jwt) {
            DthSessionStorage.setGenericItem(LICENSE_JWT_KEY, jwt);
        }
        return await verifyJwt(jwt!, LicenseManager.publicKey!);
    }

    /**
     * Ottiene e verifica la licenza post-login (validateLicense), salva il JWT aggiornato.
     * @param props Parametri per validateLicense
     */
    public static async validateAndStoreLoginLicenseJwt(props: Omit<IDthValidateLicenseRequest, "bundleId" | "domain">)
        : Promise<LicenseJWTPayload> {
        const client = DthHttpLicenseClient.getInstance();
        const res = await client.validateLicense(
            { ...props, bundleId: LicenseManager.bundleId!, domain: LicenseManager.domain! } as IDthValidateLicenseRequest
        );
        const jwt = res.jwt;
        if (jwt) {
            DthSessionStorage.setGenericItem(LICENSE_JWT_LOGIN_KEY, jwt);
        }
        return await verifyJwt(jwt!, LicenseManager.publicKey!);
    }

    /**
     * Controlla la validità della licenza a partire dal payload del JWT.
     * @param licensePayload Il payload del JWT di licenza da verificare
     * @returns true se la licenza è valida, false altrimenti
     */
    public static checkLicenseValidity(licensePayload: LicenseJWTPayload): boolean {
        // Controlla se il campo "licensed" è true
        if (!licensePayload.licensed) {
            return false;
        }

        return true;
    }

    // /**
    //  * Estrae un messaggio di errore user-friendly da un errore generico, se possibile.
    //  * @param error L'errore da cui estrarre il messaggio
    //  * @returns Il messaggio di errore user-friendly, o null se non disponibile
    //  */
    // public static tryGetLicenseErrorMessage(error: Error): string {
    //     let errorMessage: string = _t("//error.error_getting_license");
    //     if (error instanceof HttpResponseError) {
    //         const code = error.response.data?.error?.code;
    //         if (code && code.startsWith("LIC-")) {
    //             switch (code) {
    //                 case "LIC-001": // License not found 
    //                 case "LIC-002": // License Expired
    //                 case "LIC-003": // Invalid telemetry token
    //                 case "LIC-101": // User limit exceeded
    //                     break;
    //                 case "LIC-005": // App mismatch 
    //                 case "LIC-006": // Unknown bundle ID
    //                     errorMessage = _t("//error.generic_invalid_license");
    //                     break;
    //                 default:
    //                     errorMessage = _t("//error.invalid_license_contact_admin");
    //                     break;
    //             }
    //         }
    //     } else if (error instanceof LicenseError) {
    //         throw new LicenseError(_t("//error.error_validation_license"));
    //     }
    //     return errorMessage;
    // }

}